/**
 * Decodes common HTML entities that might appear in URLs.
 * This is particularly important for URLs copied from email clients
 * that may HTML-encode ampersands and other special characters.
 */
const decodeHtmlEntities = (str: string): string => {
  return str
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
};

/**
 * Parses invite parameters from the current URL's query string.
 * Handles HTML-encoded ampersands (&amp;) that can occur when users
 * copy-paste URLs from HTML-rendered emails instead of clicking links.
 *
 * @returns Object containing email and code parameters (empty strings if not present)
 *
 * @example
 * // URL: /org/invite?email=user@example.com&code=abc123
 * const { email, code } = parseInviteParams();
 * // Returns: { email: 'user@example.com', code: 'abc123' }
 *
 * @example
 * // URL: /org/invite?email=user%40example.com&amp;code=abc123 (HTML-encoded)
 * const { email, code } = parseInviteParams();
 * // Returns: { email: 'user@example.com', code: 'abc123' }
 */
export const parseInviteParams = (): { email: string; code: string } => {
  const search = decodeHtmlEntities(window.location.search);
  const params = new URLSearchParams(search);
  return {
    email: params.get('email') || '',
    code: params.get('code') || '',
  };
};
